import { setPageMeta } from '../seo'
import React, { useEffect, useState } from 'react'
import SiteFooter from '../components/SiteFooter'

// The diary reads /assets/hlo/diary.json rather than a file inside src, so an
// entry can be added without rebuilding and redeploying the site.
//
// Entries show collapsed: a thumbnail, the date, the title and the first three
// lines, with "Read more". One entry is open at a time. A link to an entry
// (/producers-diary#its-slug) opens that one on arrival.

// How many entries show before the "Earlier entries" button.
const PAGE = 10

// A YouTube link in any of its forms becomes an embed. Shorts are shown
// upright; everything else 16:9.
const youtube = (url) => {
  if (!url) return null
  const m = String(url).match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  )
  if (!m) return null
  return { id: m[1], vertical: /\/shorts\//.test(url) }
}

const DiaryVideo = ({ url }) => {
  const v = youtube(url)
  if (!v) return null
  return (
    <div className={`diary-video ${v.vertical ? 'diary-video--vertical' : ''}`}>
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${v.id}`}
        title='Video'
        loading='lazy'
        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
        allowFullScreen
      />
    </div>
  )
}

// the same anchor hlo.php gives each entry, so a link to one works either way
const slug = (t) => (String(t || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')) || 'entry'

// The picture on the collapsed card: the entry's first photograph, or the
// video's own thumbnail from YouTube when there is no photograph.
const thumbOf = (entry) => {
  const first = entry.images && entry.images[0]
  if (first && first.src) return { src: first.src, alt: first.alt || '', video: false }
  const v = youtube(entry.video)
  if (v) return { src: `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`, alt: '', video: true }
  return null
}

const DiaryEntry = ({ entry, open, onToggle }) => {
  const id = slug(entry.title)
  const body = (entry.body || []).map((para) => <p key={para.slice(0, 48)}>{para}</p>)

  if (open) {
    return (
      <li className='diary-entry is-open' id={id}>
        <span className='diary-date'>{entry.date}</span>
        <h2>{entry.title}</h2>
        {entry.video && <DiaryVideo url={entry.video} />}
        {entry.images && entry.images.length > 0 && (
          <div className={`diary-images diary-images--${entry.images.length === 1 ? 'one' : 'many'}`}>
            {entry.images.map((img) => (
              <figure key={img.src}>
                <img src={img.src} alt={img.alt || ''} loading='lazy' />
                {img.caption && <figcaption>{img.caption}</figcaption>}
              </figure>
            ))}
          </div>
        )}
        <div className='diary-body'>{body}</div>
        {entry.place && <p className='diary-place'>{entry.place}</p>}
        <button type='button' className='diary-toggle' aria-expanded='true' aria-controls={id} onClick={onToggle}>
          Close
        </button>
      </li>
    )
  }

  const thumb = thumbOf(entry)
  return (
    <li className='diary-entry' id={id}>
      <div className={`diary-row${thumb ? '' : ' diary-row--text'}`} onClick={onToggle}>
        {thumb && (
          <div className={`diary-thumb${thumb.video ? ' diary-thumb--video' : ''}`}>
            <img src={thumb.src} alt={thumb.alt} loading='lazy' />
          </div>
        )}
        <div className='diary-text'>
          <span className='diary-date'>{entry.date}</span>
          <h2>{entry.title}</h2>
          <div className='diary-body'>{body}</div>
          <button
            type='button'
            className='diary-toggle'
            aria-expanded='false'
            aria-controls={id}
            onClick={(e) => { e.stopPropagation(); onToggle() }}
          >
            Read more
          </button>
        </div>
      </div>
    </li>
  )
}

const hashId = () => {
  try { return decodeURIComponent(window.location.hash.slice(1)) } catch (err) { return '' }
}

const ProducersDiary = () => {
  // hlo.php writes the entries into the page before this runs, so the first
  // paint is the diary, not "Loading". The fetch below still refreshes them.
  const preloaded = typeof window !== 'undefined' && Array.isArray(window.__HLO_DIARY__)
    ? window.__HLO_DIARY__
    : null
  const [entries, setEntries] = useState(preloaded)
  const [failed, setFailed] = useState(false)
  const [shown, setShown] = useState(PAGE)
  // the one open entry, by its slug; a link to an entry opens it on arrival
  const [openId, setOpenId] = useState(() => (typeof window !== 'undefined' ? hashId() : ''))

  useEffect(() => {
    setPageMeta({
      title: 'Producer’s Diary | Hands Like Ours',
      path: '/handslikeours/producers-diary',
      description: 'Notes from the making of Hands Like Ours, written on the road in North Sumatra by Elaine, Ian and Hillary.',
    })
    if (!hashId()) window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    let live = true
    // cache-busted, or a new entry sits behind the old file for hours
    fetch(`/assets/hlo/diary.json?v=${Date.now()}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (!live) return
        const list = Array.isArray(data) ? data : data.entries || []
        setEntries(list)
      })
      .catch(() => live && setFailed(true))
    return () => { live = false }
  }, [])

  // If the linked entry sits past the first page, show enough pages to reach it.
  useEffect(() => {
    if (!entries || !openId) return
    const at = entries.findIndex((en) => slug(en.title) === openId)
    if (at >= shown) setShown(Math.ceil((at + 1) / PAGE) * PAGE)
  }, [entries, openId, shown])

  // After an entry opens, bring its top into view if it is above the screen
  // (closing the one above it moves everything up).
  useEffect(() => {
    if (!openId) return
    const el = document.getElementById(openId)
    if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView({ block: 'start' })
  }, [openId, entries, shown])

  const toggle = (id) => {
    const next = openId === id ? '' : id
    setOpenId(next)
    // keep the address shareable, without jumping the page
    try {
      window.history.replaceState(null, '', next ? `#${next}` : window.location.pathname)
    } catch (err) { /* nothing to do */ }
  }

  return (
    <div className='App page-standard page-hlo page-diary'>
      <header className='diary-header'>
        <div className='content-container'>
          <a className='diary-back' href='/handslikeours'>Hands Like Ours</a>
          <a className='diary-mark-link' href='/handslikeours' aria-label='Hands Like Ours'>
            <img
              className='diary-mark'
              src='/assets/hlo/hands-like-ours-mark.png'
              alt=''
              width='560'
              height='560'
            />
          </a>
          <h1>Producer&rsquo;s Diary</h1>
          <p className='diary-sub'>
            Notes from the making of the film, written on the road.
          </p>
        </div>
      </header>

      <main>
        <section data-tone='dark'>
          <div className='content-container'>
            {entries === null && !failed && (
              <p className='diary-empty'>Loading&hellip;</p>
            )}

            {(failed || (entries && entries.length === 0)) && (
              <p className='diary-empty'>
                The first entries will be written on the recce in North Sumatra.
                Follow along on{' '}
                <a href='https://www.instagram.com/thegirlandherpiano' target='_blank' rel='noopener noreferrer'>Instagram</a>
                {' '}in the meantime.
              </p>
            )}

            {entries && entries.length > 0 && (
              <>
                <ul className='diary-list'>
                  {entries.slice(0, shown).map((entry) => {
                    const id = slug(entry.title)
                    return (
                      <DiaryEntry
                        key={entry.date + entry.title}
                        entry={entry}
                        open={openId === id}
                        onToggle={() => toggle(id)}
                      />
                    )
                  })}
                </ul>
                {entries.length > shown && (
                  <p className='diary-more'>
                    <button type='button' onClick={() => setShown(shown + PAGE)}>
                      Earlier entries
                    </button>
                  </p>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <SiteFooter
        mark='/assets/hlo/hands-like-ours-mark.png'
        markHover='/assets/hlo/hands-like-ours-mark.png'
        film='hlo'
      />
    </div>
  )
}

export default ProducersDiary
